import React, { useState, useEffect } from "react";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Divider,
	IconButton,
	Grid,
	Card,
	CardContent,
} from "@mui/material";
import {
	History as HistoryIcon,
	PlayArrow as ResumeIcon,
	Download as DownloadIcon,
	Refresh as RefreshIcon,
	Delete as DeleteIcon,
	Replay as RetryIcon,
	Cancel as CancelIcon,
	Visibility as ViewIcon,
	Close as CloseIcon,
	Description as FileIcon,
	Map as ZoneIcon,
	Warehouse as FacilityIcon,
	BarChart as SummaryIcon,
	ErrorOutline as ErrorIcon,
	Timeline as ProgressIcon,
} from "@mui/icons-material";
import { getJobs, downloadFile, deleteJob, cancelJob } from "./services/api";

const STAGE_COLORS = {
	UPLOADED: "info",
	PROCESSING: "warning",
	COMPLETED: "success",
	FAILED: "error",
	CANCELLED: "default",
};

function JobDetailDialog({ job, open, onClose, formatIST }) {
	if (!job) return null;

	const summary = job.result?.summary || {};
	const zoneMapping = job.zone_mapping || {};
	const facilityMapping = job.facility_mapping || {};
	const progress = job.progress || [];

	const summaryCards = [
		{ label: "Total Required", value: (summary.total_required ?? 0).toLocaleString() },
		{ label: "Total Allocated", value: (summary.total_allocated ?? 0).toLocaleString() },
		{ label: "Allocation Rate", value: `${summary.allocation_percentage ?? 0}%` },
		{ label: "Fully Allocated", value: (summary.fully_allocated ?? 0).toLocaleString() },
		{ label: "Partial Allocated", value: (summary.partial_allocated ?? 0).toLocaleString() },
		{ label: "No Stock", value: (summary.no_stock ?? 0).toLocaleString() },
	];

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
				<Box>
					<Typography variant="h6" sx={{ fontWeight: 700 }}>
						Job Details
					</Typography>
					<Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 480 }}>
						{job.input_filename || "—"}
					</Typography>
				</Box>
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<Divider />
			<DialogContent sx={{ pt: 2 }}>
				{/* Basic Info */}
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
					<Chip label={job.stage || "—"} size="small" color={STAGE_COLORS[job.stage] || "default"} />
					<Chip label={`${job.total_records ?? 0} records`} size="small" variant="outlined" />
					{job.regions?.map((r) => (
						<Chip key={r} label={r} size="small" variant="outlined" color="info" />
					))}
					{job.user_email && (
						<Chip label={job.user_email} size="small" variant="outlined" />
					)}
				</Box>

				<Grid container spacing={1.5} sx={{ mb: 2 }}>
					<Grid item xs={6}>
						<Typography variant="caption" color="text.secondary">Created</Typography>
						<Typography variant="body2">{formatIST(job.created_at)}</Typography>
					</Grid>
					<Grid item xs={6}>
						<Typography variant="caption" color="text.secondary">Updated</Typography>
						<Typography variant="body2">{formatIST(job.updated_at)}</Typography>
					</Grid>
				</Grid>

				<Divider sx={{ my: 2 }} />

				{/* Zone Mapping */}
				{Object.keys(zoneMapping).length > 0 && (
					<>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
							<ZoneIcon sx={{ fontSize: 18, color: "primary.main" }} />
							<Typography variant="subtitle2" fontWeight={700}>Zone Mapping</Typography>
						</Box>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
							{Object.entries(zoneMapping).map(([zone, warehouses]) => (
								<Box key={zone} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
									<Chip label={zone} size="small" color="primary" sx={{ minWidth: 80 }} />
									<Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>→</Typography>
									{(warehouses || []).map((wh, i) => (
										<Chip
											key={wh}
											label={`${i + 1}. ${wh}`}
											size="small"
											variant={i === 0 ? "filled" : "outlined"}
											color={i === 0 ? "primary" : "default"}
											sx={{ fontSize: "0.7rem" }}
										/>
									))}
								</Box>
							))}
						</Box>
						<Divider sx={{ my: 2 }} />
					</>
				)}

				{/* Facility Mapping */}
				{Object.keys(facilityMapping).length > 0 && (
					<>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
							<FacilityIcon sx={{ fontSize: 18, color: "primary.main" }} />
							<Typography variant="subtitle2" fontWeight={700}>Selected Facilities</Typography>
						</Box>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
							{Object.entries(facilityMapping).map(([code, subs]) => (
								<Box key={code} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
									<Chip label={code} size="small" color="primary" sx={{ minWidth: 80 }} />
									<Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>→</Typography>
									{(subs || []).length > 0 ? (subs || []).map((s) => (
										<Chip key={s} label={s} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
									)) : (
										<Typography variant="caption" color="text.disabled">None selected</Typography>
									)}
								</Box>
							))}
						</Box>
						<Divider sx={{ my: 2 }} />
					</>
				)}

				{/* Result Summary (COMPLETED) */}
				{job.stage === "COMPLETED" && summary.total_required != null && (
					<>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
							<SummaryIcon sx={{ fontSize: 18, color: "success.main" }} />
							<Typography variant="subtitle2" fontWeight={700}>Allocation Summary</Typography>
						</Box>
						<Grid container spacing={1.5} sx={{ mb: 2 }}>
							{summaryCards.map((c) => (
								<Grid item xs={6} sm={4} key={c.label}>
									<Card variant="outlined" sx={{ p: 1.5 }}>
										<Typography variant="caption" color="text.secondary" display="block">{c.label}</Typography>
										<Typography variant="subtitle1" fontWeight={700}>{c.value}</Typography>
									</Card>
								</Grid>
							))}
						</Grid>
						{summary.allocation_by_warehouse && Object.keys(summary.allocation_by_warehouse).length > 0 && (
							<Box sx={{ mb: 2 }}>
								<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
									Allocation by Warehouse
								</Typography>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
									{Object.entries(summary.allocation_by_warehouse)
										.sort((a, b) => b[1] - a[1])
										.map(([wh, qty]) => (
											<Chip
												key={wh}
												label={`${wh}: ${qty.toLocaleString()}`}
												size="small"
												color="success"
												variant="outlined"
											/>
										))}
								</Box>
							</Box>
						)}
						<Divider sx={{ my: 2 }} />
					</>
				)}

				{/* Error (FAILED) */}
				{job.stage === "FAILED" && job.result?.error && (
					<>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
							<ErrorIcon sx={{ fontSize: 18, color: "error.main" }} />
							<Typography variant="subtitle2" fontWeight={700} color="error.main">Error</Typography>
						</Box>
						<Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "error.50", borderColor: "error.200" }}>
							<Typography variant="body2" sx={{ fontFamily: "monospace", color: "error.dark", wordBreak: "break-all" }}>
								{job.result.error}
							</Typography>
						</Paper>
						<Divider sx={{ my: 2 }} />
					</>
				)}

				{/* Progress Log */}
				{progress.length > 0 && (
					<>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
							<ProgressIcon sx={{ fontSize: 18, color: "primary.main" }} />
							<Typography variant="subtitle2" fontWeight={700}>Progress Log</Typography>
						</Box>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
							{progress.map((entry, i) => (
								<Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
									<Chip
										label={entry.process || entry.label || "—"}
										size="small"
										color={
											entry.process === "COMPLETED" ? "success"
											: entry.process === "FAILED" ? "error"
											: entry.process === "CANCELLED" ? "default"
											: entry.process === "PROCESSING" ? "warning"
											: "default"
										}
										sx={{ minWidth: 100, fontSize: "0.7rem" }}
									/>
									<Box sx={{ flex: 1 }}>
										{entry.message && (
											<Typography variant="caption" color="text.secondary">
												{entry.message}
											</Typography>
										)}
										{entry.created_at && (
											<Typography variant="caption" color="text.disabled" display="block">
												{formatIST(entry.created_at)}
											</Typography>
										)}
									</Box>
								</Box>
							))}
						</Box>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="outlined" size="small">Close</Button>
			</DialogActions>
		</Dialog>
	);
}

function History({ onResumeJob, refreshKey }) {
	const [jobs, setJobs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [expanded, setExpanded] = useState(true);
	const [deletingId, setDeletingId] = useState(null);
	const [cancellingId, setCancellingId] = useState(null);
	const [viewJob, setViewJob] = useState(null);

	const fetchJobs = async () => {
		setLoading(true);
		try {
			const res = await getJobs({ limit: 50, offset: 0 });
			if (res.data?.success) {
				setJobs(res.data.jobs || []);
			}
		} catch (err) {
			console.error("Error fetching jobs:", err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchJobs();
	}, [refreshKey]);

	const handleDownload = async (filename) => {
		try {
			const res = await downloadFile(filename);
			const blob = new Blob([res.data]);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error("Download failed:", err);
		}
	};

	const handleResume = (job) => {
		onResumeJob?.(job);
	};

	const handleCancel = async (job) => {
		if (!window.confirm(`Cancel the running job "${job.input_filename || "this job"}"?`)) return;
		setCancellingId(job.job_id);
		try {
			await cancelJob(job.job_id);
			await fetchJobs();
		} catch (err) {
			console.error("Cancel failed:", err);
			if (err.response?.data?.error) window.alert(err.response.data.error);
		} finally {
			setCancellingId(null);
		}
	};

	const handleDelete = async (job) => {
		if (!window.confirm(`Delete "${job.input_filename || "this job"}"? This cannot be undone.`)) return;
		setDeletingId(job.job_id);
		try {
			await deleteJob(job.job_id);
			await fetchJobs();
		} catch (err) {
			console.error("Delete failed:", err);
			if (err.response?.data?.error) window.alert(err.response.data.error);
		} finally {
			setDeletingId(null);
		}
	};

	const formatIST = (isoStr) => {
		if (!isoStr) return "—";
		try {
			const s = String(isoStr).trim();
			// Backend sends UTC: ensure we parse as UTC then show in IST
			const isUtc = s.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(s);
			const d = new Date(isUtc ? s : s + "Z");
			return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
		} catch {
			return isoStr;
		}
	};

	return (
		<Paper variant="outlined" sx={{ mt: 4, overflow: "hidden" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 2,
					py: 1.5,
					bgcolor: "action.hover",
					cursor: "pointer",
				}}
				onClick={() => setExpanded((e) => !e)}
			>
				<Box display="flex" alignItems="center" gap={1}>
					<HistoryIcon color="action" />
					<Typography variant="subtitle1" fontWeight="bold">
						History
					</Typography>
					<Typography variant="body2" color="text.secondary">
						({jobs.length} job{jobs.length !== 1 ? "s" : ""})
					</Typography>
				</Box>
				<Button
					size="small"
					startIcon={<RefreshIcon />}
					onClick={(e) => {
						e.stopPropagation();
						fetchJobs();
					}}
					disabled={loading}
				>
					Refresh
				</Button>
			</Box>
			<Collapse in={expanded}>
				<TableContainer>
					{loading ? (
						<Box display="flex" justifyContent="center" py={4}>
							<CircularProgress size={32} />
						</Box>
					) : jobs.length === 0 ? (
						<Box py={4} textAlign="center">
							<Typography color="text.secondary">
								No allocation history yet. Upload a file to get started.
							</Typography>
						</Box>
					) : (
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Filename</TableCell>
									{jobs.some((j) => j.user_email) && <TableCell>User</TableCell>}
									<TableCell>Stage</TableCell>
									<TableCell>Records</TableCell>
									<TableCell>Created</TableCell>
									<TableCell>Updated</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{jobs.map((job) => (
									<TableRow key={job.job_id}>
										<TableCell>
											<Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
												{job.input_filename || "—"}
											</Typography>
										</TableCell>
										{jobs.some((j) => j.user_email) && (
											<TableCell>
												<Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
													{job.user_email || "—"}
												</Typography>
											</TableCell>
										)}
										<TableCell>
											<Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
												<Chip
													label={job.stage || "—"}
													size="small"
													color={STAGE_COLORS[job.stage] || "default"}
												/>
												{job.deleted && (
													<Chip label="Deleted" size="small" color="default" variant="outlined" />
												)}
											</Box>
										</TableCell>
										<TableCell>{job.total_records ?? "—"}</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{formatIST(job.created_at)}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{formatIST(job.updated_at)}
											</Typography>
										</TableCell>
										<TableCell align="right">
										<Button
											size="small"
											startIcon={<ViewIcon />}
											onClick={() => setViewJob(job)}
											sx={{ mr: 0.5 }}
										>
											View
										</Button>
									{job.stage === "UPLOADED" && !job.deleted && (
											<>
												<Button
													size="small"
													startIcon={<ResumeIcon />}
													onClick={() => handleResume(job)}
												>
													Resume
												</Button>
												<Button
													size="small"
													color="error"
													startIcon={<DeleteIcon />}
													onClick={() => handleDelete(job)}
													disabled={deletingId === job.job_id}
													sx={{ ml: 1 }}
												>
													{deletingId === job.job_id ? "Deleting…" : "Delete"}
												</Button>
											</>
										)}
										{job.stage === "PROCESSING" && !job.deleted && (
											<Button
												size="small"
												color="error"
												variant="outlined"
												startIcon={<CancelIcon />}
												onClick={() => handleCancel(job)}
												disabled={cancellingId === job.job_id}
											>
												{cancellingId === job.job_id ? "Cancelling…" : "Cancel"}
											</Button>
										)}
										{job.stage === "FAILED" && !job.deleted && (
											<Button
												size="small"
												color="warning"
												startIcon={<RetryIcon />}
												onClick={() => handleResume(job)}
											>
												Retry
											</Button>
										)}
											{job.deleted && (
												<Typography variant="caption" color="text.secondary">
													Deleted
												</Typography>
											)}
											{job.stage === "COMPLETED" && (job.result?.output_file || job.output_file_path) && (
												<Button
													size="small"
													startIcon={<DownloadIcon />}
													onClick={() =>
														handleDownload(
															job.result?.output_file ||
																job.output_file_path?.split("/").pop() ||
																""
														)
													}
												>
													Download
												</Button>
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</TableContainer>
			</Collapse>

		<JobDetailDialog
			job={viewJob}
			open={Boolean(viewJob)}
			onClose={() => setViewJob(null)}
			formatIST={formatIST}
		/>
		</Paper>
	);
}

export default History;
